import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FilterIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { isFreeUser } from './utils/planAccess';
import { FREE_SCENARIO_IDS } from './utils/freeScenarioIds';

const UserConversationsPage = () => {
    const [conversations, setConversations] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('newest');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const [isFreePlan, setIsFreePlan] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const defaultToReports = queryParams.get('view') === 'reports';
    const sortOptionLabel = {
      newest: 'Newest First',
      oldest: 'Oldest First',
      az: 'Title A–Z',
      report: 'Has Report',
    };
    
    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    //check if free user
    useEffect(() => {
        const checkPlan = async () => {
          const free = await isFreeUser();
          setIsFreePlan(free);
        };
        checkPlan();
      }, []);

    useEffect(() => {
    if (defaultToReports) {
        setSortOption('report');
    }
    }, [defaultToReports]);

    //fetch all conversations
    useEffect(() => {
        const fetchConversations = async () => {
        try {
            const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/conversations/get-all-conversations`,
            {
                headers: {
                Authorization: `Bearer ${token}`,
                },
            }
            );
            if (!response.ok) throw new Error('Failed to fetch conversations');
            const data = await response.json();
    
            // ✅ Sort from newest to oldest based on createdAt
            const sortedData = data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
    
            setConversations(sortedData);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
        };
    
        fetchConversations();
    }, [token]);
  
    const getDifficultyLabel = (level) => {
        if (level == null) return 'Unknown';
      
        // Normalize to a number when possible (handles "4" as well as 4)
        const n = Number(level);
        if (Number.isFinite(n)) {
          if (n <= 2) return 'Beginner';
          if (n === 3) return 'Intermediate';
          if (n >= 4) return 'Advanced';
        }
      
        // Fallback if backend ever stores a text label
        const s = String(level).trim().toLowerCase();
        if (s.includes('begin')) return 'Beginner';
        if (s.includes('inter')) return 'Intermediate';
        if (s.includes('adv')) return 'Advanced';
      
        return 'Unknown';
      };
      
    const toggleExpanded = (id) => {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const confirmDelete = async () => {
        try {
          await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/conversations/${pendingDeleteId}`,
            {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setConversations((prev) =>
            prev.filter((c) => c.conversationId !== pendingDeleteId)
          );
        } catch (err) {
          console.error('Delete failed', err);
        } finally {
          setIsModalOpen(false);
          setPendingDeleteId(null);
        }
      };
      
    const cancelDelete = () => {
        setIsModalOpen(false);
        setPendingDeleteId(null);
    };

    const handleContinue = (conversation) => {
        navigate('/simulator', {
        state: {
            conversationId: conversation.conversationId,
            scenarioId: conversation.scenarioId,
            isCustomScenario: conversation.isCustomScenario,
            fromPage: true,
        },
        });
    };

    const handleReport = (conversationId) => {
        navigate(`/report/${conversationId}`);
    };

    //set up conversation continue button for if free user and non-free scenario
    const renderContinueButton = (conversation) => {
        const isPaidScenario = !conversation.isCustomScenario && !FREE_SCENARIO_IDS.includes(conversation.scenarioId);
      
        if (isFreePlan && isPaidScenario) {
            return (
              <div className="relative group inline-block">
                <a
                  href="/pricing"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm px-3 py-1 bg-gray-200 text-gray-500 rounded cursor-pointer w-full block text-center"
                >
                  Continue
                </a>
                <div className="absolute left-1/2 transform -translate-x-1/2 mt-1 w-64 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 text-center break-words">
                    Premium conversation. Upgrade required to continue conversation
                </div>
              </div>
            );
          }
      
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleContinue(conversation);
            }}
            className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
          >
            Continue
          </button>
        );
      };      

    // Filter and sort conversations based on search term and selected sort option
    const filteredConversations = conversations
        .filter((conv) => {
            const search = searchTerm.toLowerCase();
            return (
            conv.scenarioTitle.toLowerCase().includes(search) ||
            (conv.employeeName && conv.employeeName.toLowerCase().includes(search))
            );
        })
        .sort((a, b) => {
            if (sortOption === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
            if (sortOption === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
            if (sortOption === 'az') return a.scenarioTitle.localeCompare(b.scenarioTitle);
            if (sortOption === 'report') return (b.hasReport === true) - (a.hasReport === true);
            return 0;
        });

    // Show loading state if conversations are still being fetched
    if (loading) return (
        <div className="flex flex-col items-center justify-center py-16 text-gray-700">
            <svg
            className="animate-spin h-6 w-6 mb-3 text-green-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span className="text-lg font-medium">Loading conversations...</span>
        </div>
        );
          

    const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
        if (!isOpen) return null;
      
        return (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Confirm Deletion</h2>
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to delete this conversation and feedback report?
                        <br />
                        <br />
                        This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                        onClick={onConfirm}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
                        >
                        Delete
                        </button>
                        <button
                        onClick={onCancel}
                        className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
                        >
                        Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };
      
    return (
        <div className="w-full min-w-[460px] max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
            <h1 className="text-3xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200">Your Conversations</h1>

            {/* Search and Sort Controls */}
            <div className="flex items-center justify-between mb-4">
                <input
                    type="text"
                    placeholder="Search by title or employee name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border px-3 py-2 rounded w-2/3 md:w-1/2"
                />

                <div className="relative inline-block text-left ml-4">
                    <button
                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                        className="inline-flex justify-center items-center gap-2 border px-3 py-2 rounded bg-white shadow-sm hover:bg-gray-50"
                    >
                        <FilterIcon className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-700">Sort: {sortOptionLabel[sortOption]}</span>
                        <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    </button>

                    {sortDropdownOpen && (
                        <div className="absolute z-10 mt-2 w-48 bg-white shadow-lg rounded-md border">
                            {Object.entries(sortOptionLabel).map(([value, label]) => (
                                <button
                                    key={value}
                                    onClick={() => {
                                    setSortOption(value);
                                    setSortDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Conversations List */}
            {filteredConversations.map((conv) => {
                const hasReport = !!conv.hasReport;
                return (
                    
                    <div
                        key={conv.conversationId}
                        className="border border-gray-200 rounded-xl shadow-sm mb-4 hover:shadow-md transition-shadow duration-200"
                    >
                        <div
                            className="flex flex-wrap justify-between items-center gap-4 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-xl cursor-pointer"
                            onClick={() => toggleExpanded(conv.conversationId)}
                            >
                            <div>
                                <h2 className="text-lg min-w-[400px] font-semibold text-gray-800">
                                    {conv.scenarioTitle}
                                    {conv.employeeName && (
                                    <span className="text-sm text-gray-500 font-normal ml-2">({conv.employeeName})</span>
                                    )}
                                </h2>
                                <p className="text-sm text-gray-500 flex items-center gap-2">
                                    {new Date(conv.createdAt).toLocaleString()}
                                    <span className="bg-white border border-gray-300 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                                    {getDifficultyLabel(conv.difficultyLevel)}
                                    </span>
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (hasReport) handleReport(conv.conversationId);
                                    }}
                                    className={`text-sm px-3 py-1 rounded transition ${
                                        hasReport
                                        ? 'bg-gray-200 text-purple-700 hover:bg-gray-300'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                    disabled={!hasReport}
                                    >
                                    Report
                                </button>
                                <div onClick={(e) => e.stopPropagation()}>
                                    {renderContinueButton(conv)}
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPendingDeleteId(conv.conversationId);
                                        setIsModalOpen(true);
                                    }}
                                    className="text-sm px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                    >
                                    Delete
                                </button>
                            </div>
                        </div>

                        {expanded[conv.conversationId] && conv.history && (
                            <div className="bg-white border-t border-gray-200 p-4 text-sm text-gray-700">
                                {/* Scenario Summary */}
                                {conv.scenarioSummary && (
                                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-gray-800">
                                    <strong className="block mb-1 text-purple-700">Scenario Summary:</strong>
                                    <div className="whitespace-pre-line">{conv.scenarioSummary}</div>
                                </div>
                                )}

                                {/* Conversation History */}
                                <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                                {conv.history.map((msg, idx) => (
                                    <div key={idx} className="flex gap-2">
                                    <span className="font-medium text-gray-600 capitalize">
                                        {msg.role === 'user' ? 'You' : conv.employeeName}:
                                    </span>
                                    <span>{msg.content}</span>
                                    </div>
                                ))}
                                </div>
                            </div>
                            )}
                    </div>
                );
            })}

            <ConfirmationModal
                isOpen={isModalOpen}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />

        </div>
    );
  
};

export default UserConversationsPage;
